import { getAnalytics } from "@tm/insurance/analytics";
import { downloadPdfPost } from "@tm/insurance/utils";
import { CommonModalDweb, FormattedText } from "@tm/static";
import React, { useEffect, useState } from "react";
import { TextNodeType } from "../FormattedText/FormattedText";
import {
  ModalContent,
  OptionContent,
  OptionItem,
  OptionsContainer,
  OptionTitle,
  PolicyDescription,
  RadioButton,
  RadioButtonDot,
} from "./DownloadPolicyModal.style";
import PrimaryButton from "./shared/PrimaryButton";

export interface DownloadBenefitsOption {
  id?: string;
  label?: TextNodeType;
  defaultSelected?: boolean;
  templateType?: string; // Maps to "with_price" or "without_price"
}

export interface DownloadBenefitsButton {
  id?: string;
  label?: TextNodeType;
  type?: string;
  buttonStyles?: {
    backgroundColor?: string;
    border?: string;
    padding?: string;
    borderRadius?: string;
  };
}

export interface DownloadBenefitsModalData {
  header?: {
    title?: TextNodeType;
  };
  content?: {
    description?: TextNodeType;
    options?: DownloadBenefitsOption[];
  };
  footer?: {
    buttons?: DownloadBenefitsButton[];
  };
}

export interface DownloadPolicyModalProps {
  data?: DownloadBenefitsModalData;
  quoteId?: string; // Add quoteId for the new API endpoint
  isOpen?: boolean;
  onClose?: () => void;
  csrfToken?: string;
}

const DownloadPolicyModal: React.FC<DownloadPolicyModalProps> = ({
  data,
  quoteId,
  isOpen,
  onClose,
  csrfToken,
}) => {
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(0);
  const analytics = getAnalytics();
  // Set the selected option based on defaultSelected when data changes
  useEffect(() => {
    if (data?.content?.options) {
      // Find the last option with defaultSelected: true
      // In case multiple options have defaultSelected: true, we take the last one
      let defaultIndex = 0;
      data.content.options.forEach((option, index) => {
        if (option.defaultSelected) {
          defaultIndex = index;
        }
      });
      setSelectedOptionIndex(defaultIndex);
    }
  }, [data]);

  // Ensure portal element exists
  useEffect(() => {
    if (!document.getElementById("portal")) {
      const portalDiv = document.createElement("div");
      portalDiv.id = "portal";
      document.body.appendChild(portalDiv);
    }
  }, []);

  if (!isOpen || !data) {
    return null;
  }

  const handleOptionSelect = (index: number) => {
    setSelectedOptionIndex(index);
  };

  const handleDownload = async () => {
    const selectedOption = data?.content?.options?.[selectedOptionIndex];
    const itineraryId =
      new URLSearchParams(window.location.search).get("itineraryId") || "";
    if (selectedOption && quoteId) {
      const templateType = selectedOption.id || "with_price";
      analytics?.trackListingEvents({
        actionType: `policy_downloaded_${
          templateType === "with_price" ? "with_price" : "without_price"
        }`,
      });
      await downloadPdfPost(quoteId, templateType, csrfToken, itineraryId);
      onClose?.();
    }
  };

  return (
    <CommonModalDweb
      customTitle={data.header?.title}
      handleClick={onClose}
      nonClipHeading
      customWidth="500px"
    >
      <ModalContent>
        {data.content?.description && (
          <PolicyDescription>
            <FormattedText value={data.content.description} />
          </PolicyDescription>
        )}

        {data.content?.options && (
          <OptionsContainer>
            {data.content.options.map((option, index) => {
              const keyValue = option.id || `option-${index}`;
              const isSelected = selectedOptionIndex === index;
              return (
                <OptionItem
                  key={keyValue}
                  onClick={() => handleOptionSelect(index)}
                  isSelected={isSelected}
                >
                  <RadioButton isSelected={isSelected}>
                    {isSelected && <RadioButtonDot />}
                  </RadioButton>
                  <OptionContent>
                    <OptionTitle>
                      <FormattedText value={option.label} />
                    </OptionTitle>
                  </OptionContent>
                </OptionItem>
              );
            })}
          </OptionsContainer>
        )}

        <PrimaryButton
          text={data.footer?.buttons?.[0]?.label}
          onClick={handleDownload}
          fontSize={16}
          block={true}
        />
      </ModalContent>
    </CommonModalDweb>
  );
};

export default DownloadPolicyModal;
